import { useMemo } from "react";
import { Alert, Box, Button, Group, Loader, Paper, Progress, ScrollArea, Stack, Text } from "@mantine/core";

import { formatMealLine, formatQuantityValue } from "../meal-summary-format";
import type { MacroTotals } from "@/packages/dashboard/Home/Meals/macro-aggregations";
import type { UserTargets } from "@/packages/dashboard/api/user-targets";
import { useUserTargets } from "@/packages/dashboard/UserTargets/UserTargetsProvider";
import { useDailyTargetProgress } from "./use-daily-target-progress";
import type { MealEntry } from "./use-meal-form";

const formatMealEntrySummary = (entry: MealEntry) =>
  formatMealLine(entry.quantity, entry.foodItem.serving_unit, entry.foodItem.name);

type MacroKey = keyof MacroTotals;

type MacroDisplay = {
  macroKey: MacroKey;
  label: string;
  unit: string;
};

const MACRO_CONFIG: MacroDisplay[] = [
  { macroKey: "calories", label: "Calories", unit: "kcal" },
  { macroKey: "proteins", label: "Protein", unit: "g" },
  { macroKey: "carbs", label: "Carbs", unit: "g" },
  { macroKey: "fats", label: "Fat", unit: "g" },
];

const TARGET_FIELD_BY_MACRO: Record<MacroKey, keyof UserTargets> = {
  calories: "caloriesDailyTarget",
  proteins: "proteinsDailyTarget",
  carbs: "carbsDailyTarget",
  fats: "fatsDailyTarget",
};

const calorieFormatter = new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 });
const macroFormatter = new Intl.NumberFormat(undefined, { maximumFractionDigits: 1, minimumFractionDigits: 0 });

const formatMacroAmount = (key: MacroKey, value: number) => {
  const safeValue = Number.isFinite(value) ? value : 0;
  return key === "calories" ? calorieFormatter.format(Math.round(safeValue)) : macroFormatter.format(safeValue);
};

const computeBarSections = (baseline: number, meal: number, target: number | null) => {
  const safeBaseline = Math.max(0, baseline);
  const safeMeal = Math.max(0, meal);
  const total = safeBaseline + safeMeal;
  const hasTarget = typeof target === "number" && Number.isFinite(target) && target > 0;

  if (!hasTarget) {
    const denominator = total || 1;
    return {
      baselinePct: (safeBaseline / denominator) * 100,
      mealPct: (safeMeal / denominator) * 100,
      overflowPct: 0,
    };
  }

  const targetValue = target;
  const overflow = Math.max(0, total - targetValue);
  const denominator = targetValue + overflow || 1;
  const baselineWithinTarget = Math.min(safeBaseline, targetValue);
  const mealWithinTarget = Math.max(0, Math.min(safeMeal, targetValue - baselineWithinTarget));

  return {
    baselinePct: (baselineWithinTarget / denominator) * 100,
    mealPct: (mealWithinTarget / denominator) * 100,
    overflowPct: (overflow / denominator) * 100,
  };
};

type MealBoardPanelProps = {
  mealItems: MealEntry[];
  mealTimestamp: Date | null;
  currentMealId: number | null;
  onDeleteEntry: (entry: MealEntry) => void;
};

export const MealBoardPanel = ({ mealItems, mealTimestamp, currentMealId, onDeleteEntry }: MealBoardPanelProps) => {
  const {
    data: userTargets,
    isLoading: isLoadingTargets,
    error: userTargetsError,
  } = useUserTargets();

  const dailyProgress = useDailyTargetProgress({
    mealTimestamp,
    currentMealId,
    mealEntries: mealItems,
  });

  const macroRows = useMemo(
    () =>
      MACRO_CONFIG.map((macro) => {
        const targetField = TARGET_FIELD_BY_MACRO[macro.macroKey];
        const targetValue = userTargets ? userTargets[targetField] ?? null : null;
        return {
          ...macro,
          targetValue,
          baselineValue: dailyProgress.baselineTotals[macro.macroKey],
          mealValue: dailyProgress.pendingTotals[macro.macroKey],
          projectedValue: dailyProgress.projectedTotals[macro.macroKey],
        };
      }),
    [dailyProgress.baselineTotals, dailyProgress.pendingTotals, dailyProgress.projectedTotals, userTargets],
  );

  const showTargetsPlaceholder = !isLoadingTargets && !userTargets;

  const todaysTargetsView = (
    <Stack gap="sm">
      <Stack gap={2}>
        <Text size="xs" tt="uppercase" fw={600} c="dimmed" style={{ letterSpacing: "0.08em" }}>
          Today&apos;s targets
        </Text>
        <Group justify="space-between" align="flex-start">
          <Stack gap={2}>
            <Text fw={600} size="lg" style={{ color: "#111827" }}>
              {dailyProgress.dayLabel ?? "Select meal time"}
            </Text>
          </Stack>
          {(dailyProgress.isFetching || isLoadingTargets) && <Loader size="sm" />}
        </Group>
      </Stack>

      {userTargetsError && (
        <Alert color="red" variant="light" title="Targets unavailable" radius="md">
          {userTargetsError.message || "We could not load your targets right now."}
        </Alert>
      )}

      {!dailyProgress.isDaySelected ? (
        <Text size="sm" c="dimmed">
          Pick a meal date & time to understand how today is trending relative to your calorie and macro targets.
        </Text>
      ) : showTargetsPlaceholder ? (
        <Alert color="blue" variant="light" radius="md">
          Set your calorie and macro targets to unlock projected progress while logging meals.
        </Alert>
      ) : (
        <Stack gap="md">
          {macroRows.map((macro) => (
            <MacroProgressRow key={macro.macroKey} {...macro} />
          ))}
        </Stack>
      )}
    </Stack>
  )

  return (
    <Paper
      withBorder={false}
      shadow={"none"}
      radius="md"
      p="xl"
      style={{
        minHeight: 360,
      }}
    >
      <Stack gap="lg" style={{ height: "100%" }}>
        {todaysTargetsView}

        <Stack gap={2} mt={"md"}>
          <Text size="xs" tt="uppercase" fw={600} c="dimmed" style={{ letterSpacing: "0.08em" }}>
            Foods added in this meal
          </Text>
        </Stack>

        {mealItems.length === 0 ? (
          <Stack align="flex-start" justify="center" gap="xs" style={{ flex: 1 }}>
            <Text fw={600} size="md" style={{ fontFamily: "'Georgia', 'Times New Roman', serif", color: "#111827" }}>
              No foods logged for this meal yet.
            </Text>
          </Stack>
        ) : (
          <ScrollArea style={{ flex: 1 }} type="auto">
            <Stack gap="md" pb="lg">
              {mealItems.map((entry) => {
                const mealSummary = formatMealEntrySummary(entry);

                return (
                  <Box key={entry.clientId}>
                    <Stack gap="sm">
                      <Group justify="space-between" align="flex-start" gap="sm">
                        <Text
                          size="md"
                          lh={1.6}
                          style={{
                            fontFamily: "'Georgia', 'Times New Roman', serif",
                            color: "#1F2933",
                            flex: 1,
                          }}
                        >
                          <Text component="span" fw={600}>
                            • {mealSummary}
                          </Text>
                        </Text>

                        <Button size="xs" variant="light" color="red" onClick={() => onDeleteEntry(entry)}>
                          Delete
                        </Button>
                      </Group>

                      <Text size="xs" c="dimmed">
                        {formatQuantityValue(entry.quantity)} {entry.foodItem.serving_unit}
                      </Text>
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          </ScrollArea>
        )}
      </Stack>
    </Paper>
  );
};

type MacroProgressRowProps = MacroDisplay & {
  targetValue: number | null;
  baselineValue: number;
  mealValue: number;
  projectedValue: number;
};

const MacroProgressRow = ({
  label,
  unit,
  macroKey,
  targetValue,
  baselineValue,
  mealValue,
  projectedValue,
}: MacroProgressRowProps) => {
  const { baselinePct, mealPct, overflowPct } = computeBarSections(baselineValue, mealValue, targetValue);
  const difference = typeof targetValue === "number" && !Number.isNaN(targetValue) ? targetValue - projectedValue : null;

  const status = difference === null ? null : difference >= 0 ? "remaining" : "over";
  const statusText =
    difference === null
      ? "No target set"
      : status === "remaining"
        ? `${formatMacroAmount(macroKey, difference)} ${unit} remaining`
        : `Over by ${formatMacroAmount(macroKey, Math.abs(difference))} ${unit}`;

  const statusColor =
    status === "over" ? "#B91C1C" : status === "remaining" ? "#047857" : "var(--mantine-color-gray-6)";

  return (
    <Stack gap={6}>
      <Group justify="space-between" align="center">
        <Text size="sm" fw={600}>
          {label}
        </Text>
        <Text size="sm" fw={500} c="#111827">
          {formatMacroAmount(macroKey, projectedValue)}
          {targetValue != null ? ` / ${formatMacroAmount(macroKey, targetValue)}` : ""} {unit}
        </Text>
      </Group>

      <Progress.Root size="sm" radius="xl">
        {baselinePct > 0 && <Progress.Section value={baselinePct} color="gray.4" />}
        {mealPct > 0 && <Progress.Section value={mealPct} color="deepOrange.6" />}
        {overflowPct > 0 && <Progress.Section value={overflowPct} color="red.6" />}
      </Progress.Root>

      <Group justify="space-between">
        <Text size="xs" c="dimmed">
          Logged earlier: {formatMacroAmount(macroKey, baselineValue)} {unit}
        </Text>
        <Text size="xs" c="dimmed">
          This meal: +{formatMacroAmount(macroKey, mealValue)} {unit}
        </Text>
      </Group>

      <Text size="xs" fw={500} style={{ color: statusColor }}>
        {statusText}
      </Text>
    </Stack>
  );
};
